if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        const configuration = this.querySelector('[data-media-colors]');
        if (!configuration) return;
        this.colorMedia = JSON.parse(configuration.textContent);
        if (!Object.values(this.colorMedia.groups).some((colors) => colors.length)) {
          this.colorMedia = null;
          return;
        }
        this.colorSlides = [...this.elements.viewer.slider.children].filter((item) => item.dataset.mediaId);
        this.colorSlides.forEach((slide) => {
          slide.classList.remove('scroll-trigger', 'scroll-trigger--offscreen', 'animate--fade-in', 'animate--slide-in');
        });
        this.applyColor(this.colorMedia.selected);
      }

      applyColor(color) {
        if (!this.colorMedia) return;
        if (this.appliedColor === color) return;
        this.appliedColor = color;
        this.colorMedia.selected = color;
        const visible = (id) => {
          const colors = this.colorMedia.groups[id] || [];
          return !colors.length || colors.includes(color);
        };
        const slides = this.colorSlides;
        const toggle = (element, id) => {
          const hidden = !visible(id);
          if (element.hidden !== hidden) element.hidden = hidden;
          const display = hidden ? 'none' : '';
          if (element.style.display !== display) element.style.display = display;
        };
        // The render-time stylesheet prevents a flash of the wrong color before upgrade.
        this.querySelector('[data-media-colors] + style')?.remove();
        slides.forEach((slide) => toggle(slide, slide.dataset.mediaId.split('-').pop()));
        const thumbnails = [...(this.elements.thumbnails?.querySelectorAll('[data-target]') || [])];
        thumbnails.forEach((item) => toggle(item, item.dataset.target.split('-').pop()));
        const modal = document.getElementById(this.id.replace('MediaGallery-', 'ProductModal-'));
        const modalContent = modal?.querySelector('.product-media-modal__content');
        const modalItems = [...(modalContent?.children || [])].filter((item) => item.dataset.mediaId);
        modalItems.forEach((item) => {
          item.classList.remove('product__media-item--variant');
          toggle(item, item.dataset.mediaId);
        });

        // Establish one stable DOM order so later color changes only toggle visibility.
        if (!this.colorMediaOrdered) {
          const ordered = [...slides].sort(
            (first, second) => Number(first.dataset.mediaType !== 'image') - Number(second.dataset.mediaType !== 'image')
          );
          const orderByMediaId = (items, getId) => {
            const itemsById = new Map(items.map((item) => [getId(item), item]));
            ordered.forEach((slide) => {
              const item = itemsById.get(slide.dataset.mediaId.split('-').pop());
              if (item) item.parentElement.append(item);
            });
          };
          ordered.forEach((slide) => slide.parentElement.append(slide));
          orderByMediaId(thumbnails, (item) => item.dataset.target.split('-').pop());
          orderByMediaId(modalItems, (item) => item.dataset.mediaId);
          this.colorSlides = ordered;
          this.colorMediaOrdered = true;
        }

        const visibleSlides = this.colorSlides.filter((slide) => !slide.hidden);
        this.dataset.visibleMediaCount = String(visibleSlides.length);
        this.toggleAttribute('data-single-visible-media', visibleSlides.length === 1);
        thumbnails.filter((item) => !item.hidden).forEach((item, index) => {
          item.dataset.mediaPosition = String(index + 1);
        });

        this.elements.viewer.slider?.scrollTo({ left: 0, behavior: 'instant' });
        this.elements.thumbnails?.slider?.scrollTo({ left: 0, behavior: 'instant' });
        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
        const count = visibleSlides.length;
        const controls = this.elements.viewer.querySelector('.slider-buttons');
        if (controls) controls.style.display = count < 2 ? 'none' : '';
        if (count < 2) {
          const viewer = this.elements.viewer;
          viewer.totalPages = 1;
          viewer.currentPage = 1;
          if (viewer.currentPageElement) viewer.currentPageElement.textContent = '1';
          if (viewer.pageTotalElement) viewer.pageTotalElement.textContent = String(count);
        }
        const first = visibleSlides[0];
        window.pauseAllMedia();
        slides.forEach((slide) => slide.classList.toggle('is-active', slide === first));
        if (first) {
          this.elements.viewer.slider?.scrollTo({ left: first.offsetLeft, behavior: 'instant' });
          this.setActiveThumbnail(this.elements.thumbnails?.querySelector(`[data-target="${first.dataset.mediaId}"]`));
        }
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
